import { useCustomForm } from "@/hooks/useCustomForm"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  Divider,
  IconButton,
  MenuItem,
  Autocomplete,
} from "@mui/material"
import { z } from "zod"
import SingleSelect from "@/components/Select/SingleSelect"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import React, { useEffect, useState } from "react"
import { useCrudContext } from "@/context/crudContext"
import ClearIcon from "@mui/icons-material/Clear"
import { Textarea } from "@mantine/core"
import { DevTool } from "@hookform/devtools"
import MultiSelect from "@/components/Select/MultipleSelect"
import Grid from "@mui/material/Grid2"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import useSuppliers from "@/hooks/useSuppliers"
import useTags from "@/hooks/useTags"
import useAttributes from "@/hooks/useAttributes"
import useBrands from "@/hooks/useBrands"

const ProductSchema = z.object({
  name: z.string().min(3),
  code: z.string().min(1),
  sell_price: z
    .string()
    .optional()
    .nullable()
    .refine((val) => val === null || val === undefined || !isNaN(Number(val)), {
      message: "Selling price must be a number",
    }),
  attribute_ids: z.array(z.number()),
})

const AttributeSchema = z.object({
  id: z.number().positive(),
  key: z.string(),
  attribute: z.string(),
  value: z.string().optional().nullable(),
})
const TagSchema = z.object({
  id: z.number().positive(),
  name: z.string(),
})

const ProductVariantSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters long"), // Ensures the name is not empty and has a min length of 3.
  code: z.string().min(2, "Code must be at least 2 characters long"), // Ensures the name is not empty and has a min length of 3.
  sell_price: z
    .string()
    .optional()
    .nullable()
    .refine((val) => val === null || val === undefined || !isNaN(Number(val)), {
      message: "Sell price must be a number",
    }), // Ensures sell price is a valid number or null/undefined.
  brand_id: z.number().nullable().optional(),

  products: z
    .array(ProductSchema)
    .min(1, "At least one product must be included"),

  product_attributes: z.array(AttributeSchema),
  tags: z.array(TagSchema),
})

const AddProductVariantModal = ({ open, onClose }) => {
  const queryClient = useQueryClient()
  const { openDialog, setOpenDialog, selectedRow, setSelectedRow } =
    useCrudContext()

  const {
    api,
    register,
    handleSubmit,
    errors,
    isSubmitting,
    reset,
    watch,
    setValue,
    control,
    Controller,
    getValues,
  } = useCustomForm({
    resolver: zodResolver(ProductVariantSchema),
  })
  const { attributes, isLoading: attributeLoading } = useAttributes()
  const { tags, isLoading: tagLoading } = useTags()
  const { brands, isLoading: brandLoading } = useBrands()

  const defaultOnClose = () => {
    setOpenDialog(false)
  }

  const setFormValues = (data) => {
    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            setValue(`${key}.${nestedKey}`, nestedValue)
          })
        } else {
          setValue(`${key}`, value)
        }
      })
    } else {
      reset({
        name: "",
        cost_price: "",
        sell_price: "",
        description: "",
      })
    }
  }

  const onSubmit = async (data) => {
    if (data.cost_price) {
      data.cost_price = Number(data.cost_price)
    }
    if (data.sell_price) {
      data.sell_price = Number(data.sell_price)
    }

    console.log("onsubmit", data)
    const method = selectedRow ? "PUT" : "POST" // Use PUT for updating, POST for adding
    const url = selectedRow
      ? `product-variant/${selectedRow.id}`
      : "product-variant"

    const response = await api(url, {
      method,
      body: JSON.stringify(data),
    })

    if (response) {
      queryClient.invalidateQueries({ queryKey: ["product-variant"] })
      toast.success(
        `Product Variant successfully ${selectedRow ? "updated" : "added"}`
      )
      onClose ? onClose() : defaultOnClose()
      reset()
    }
  }

  // generate product
  const products = watch("products", [])

  const generateProductCombinations = () => {
    const attributes = getValues("product_attributes")
    const productName = getValues("name")
    const productCode = getValues("code")
    const costPrice = getValues("cost_price")
    const sellPrice = getValues("sell_price")

    if (attributes === undefined || attributes.length == 0) {
      return [{}] // If no attributes, return an empty product
    }

    // Group attributes by their keys
    const groupedAttributes = attributes.reduce((acc, attr) => {
      const { key, attribute } = attr
      if (!acc[key]) acc[key] = []
      acc[key].push(attr)
      return acc
    }, {})
    const keys = Object.keys(groupedAttributes) // List of keys for attributes
    const combinations = []

    // Recursive function to generate combinations
    const createCombinations = (
      prefix = { attributeIds: [], name: "", code: "" },
      index = 0
    ) => {
      if (index === keys.length) {
        combinations.push({
          attributeIds: [...prefix.attributeIds], // Add the attribute IDs
          name: prefix.name.trim(),
          code: prefix.code, // Create combination name
        })
        return
      }
      const currentKey = keys[index] // Get the current key

      // Iterate over each attribute in the current key group
      groupedAttributes[currentKey].forEach(({ id, attribute }) => {
        createCombinations(
          {
            attributeIds: [...prefix.attributeIds, id], // Append attribute ID
            name: `${prefix.name}${currentKey}-${attribute} `,
            code: `${prefix.code}-${attribute}`, // Append attribute name
          },
          index + 1
        )
      })
    }

    createCombinations() // Start creating combinations

    // Map the generated combinations to create product data
    const generatedProduct = combinations.map((combination, index) => {
      return {
        id: index, // Unique ID for the product
        name: `${productName} ${combination.name}`, // Product name + combination name
        sell_price: sellPrice, // Sell price from form
        cost_price: costPrice, // Cost price from form
        code: `${productCode}${combination.code}`, // Product code (optional, default empty)
        stock: 0, // Initial stock (default 0)
        attribute_ids: combination.attributeIds, // Array of attribute IDs for this combination
      }
    })
    // Set the generated products in the form state
    setValue("products", generatedProduct)

    return generatedProduct
  }

  const handleProductProperties = (index, newValue, property) => {
    const currentProducts = getValues("products")
    const updatedProducts = currentProducts.map((product, i) =>
      i === index ? { ...product, [property]: newValue } : product
    )
    setValue("products", updatedProducts)
  }

  const handleRemoveProduct = (itemToRemove) => {
    const currentProducts = getValues("products")
    const updatedProducts = currentProducts.filter(
      (item) => item.id !== itemToRemove.id
    )

    console.log(currentProducts, updatedProducts)
    setValue("products", updatedProducts)
  }

  useEffect(() => {
    setFormValues(selectedRow)
  }, [selectedRow, setValue, reset])

  const product_attributes = watch("product_attributes")
  const productName = watch("name")
  const cp = watch("cost_price")
  const sp = watch("sell_price")
  const code = watch("code")

  useEffect(() => {
    generateProductCombinations()
  }, [product_attributes, productName, cp, sp, code])

  const handleReset = () => {
    setFormValues(selectedRow)
    setValue("products", [])
  }

  return (
    <Modal
      open={open ? open : openDialog}
      onClose={defaultOnClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          margin: "100px auto",
          bgcolor: "white",
          maxHeight: "90vh",
          width: "80vw",
          overflowY: "auto",
          borderRadius: 1,
          boxShadow: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: 1,
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
            }}
            className="text-black"
          >
            {selectedRow ? "Update Product Variant" : "Add New Product Variant"}
          </Typography>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "500",
              fontSize: { xs: "12px", md: "16px" },
              marginBottom: 2,
            }}
            className="text-black"
          >
            Basic Information
          </Typography>
          <TextField
            fullWidth
            label="Name"
            sx={{ marginBottom: 2 }}
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register("name")}
          />
          {/* basic information */}
          <Grid xs={12} md={12} lg={12} container spacing={2}>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                fullWidth
                label="Selling Price"
                type="number"
                sx={{ marginBottom: 2 }}
                error={!!errors.sell_price}
                inputProps={{
                  min: 0,
                  step: 0.01,
                }}
                helperText={errors.sell_price?.message}
                {...register("sell_price")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <TextField
                fullWidth
                label="Article Name"
                sx={{ marginBottom: 2 }}
                error={!!errors.code}
                helperText={errors.code?.message}
                {...register("code")}
              />
            </Grid>
            <Grid size={{ sm: 12, md: 6, lg: 4 }}>
              <MultiSelect
                name="tags"
                label="Tags"
                options={tags}
                control={control}
                loading={tagLoading}
                defaultValues={selectedRow?.tags}
                controller={Controller}
                optionLabel="name"
                error={!!errors.tags}
                helperText={errors.tags?.message}
              />
            </Grid>
            <Grid size={{ sm: 12, md: 6, lg: 4 }}>
              <SingleSelect
                name="brand_id"
                controller={Controller}
                control={control}
                loading={brandLoading}
                label="Brand"
                selectedValue={selectedRow?.brand_id}
                options={brands ?? []}
                error={!!errors.brand_id}
                helperText={errors.brand_id?.message}
                optionLabel={"name"}
                optionValue="id"
              />
            </Grid>
            <Grid size={{ sm: 12, md: 6, lg: 8 }}>
              <MultiSelect
                name="product_attributes"
                label="Attributes"
                loading={attributeLoading}
                options={attributes}
                defaultValues={selectedRow?.product_attributes}
                control={control}
                controller={Controller}
                optionLabel={["key", "attribute", "value"]}
                error={!!errors.product_attributes}
                helperText={errors.product_attributes?.message}
              />
            </Grid>
          </Grid>

          {/* generated product */}
          <Divider sx={{ marginBottom: 2 }} />
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "500",
              fontSize: { xs: "12px", md: "16px" },
              marginBottom: 2,
            }}
            className="text-black"
          >
            Products
          </Typography>
          <Controller
            name="products"
            control={control}
            defaultValue={[]}
            render={({
              field: { onChange: fieldOnChange, value: fieldValue },
              fieldState: { error, isTouched, isDirty },
            }) => (
              <>
                {fieldValue.map((product, index) => (
                  <Grid key={index} sx={{ marginTop: 1 }} container spacing={2}>
                    <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                      <FormControl
                        fullWidth
                        error={!!errors.products?.[index]?.name}
                      >
                        <TextField
                          label="Product Name"
                          value={product.name}
                          variant="filled"
                          helperText={
                            errors.products?.[index]?.name?.message || ""
                          }
                          id={`productName${index}`}
                          name="productName"
                          onChange={(event) =>
                            handleProductProperties(
                              index,
                              event.target.value,
                              "name"
                            )
                          }
                          sx={{
                            "& .MuiInputBase-root": {
                              border: "1px solid #D5D9E2",
                              backgroundColor: "#fff",
                              borderRadius: "7px",
                            },
                            "& .MuiInputBase-root::before": {
                              border: "none",
                            },
                            "& .MuiInputBase-root:hover::before": {
                              border: "none",
                            },
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                      <FormControl
                        fullWidth
                        error={!!errors.products?.[index]?.code}
                      >
                        <TextField
                          label="Code"
                          value={product.code}
                          variant="filled"
                          error={!!errors.products?.[index]?.code}
                          helperText={
                            errors.products?.[index]?.code?.message || ""
                          }
                          onChange={(event) =>
                            handleProductProperties(
                              index,
                              event.target.value,
                              "code"
                            )
                          }
                          sx={{
                            "& .MuiInputBase-root": {
                              border: "1px solid #D5D9E2",
                              backgroundColor: "#fff",
                              borderRadius: "7px",
                            },
                            "& .MuiInputBase-root::before": {
                              border: "none",
                            },
                            "& .MuiInputBase-root:hover::before": {
                              border: "none",
                            },
                          }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid size={{ sm: 12, md: 6, lg: 3 }}>
                      <FormControl
                        fullWidth
                        error={!!errors.products?.[index]?.sell_price}
                      >
                        <TextField
                          label="Sell Price"
                          value={product.sell_price}
                          variant="filled"
                          type="number"
                          id={`sellPrice${index}`}
                          name="sellPrice"
                          error={!!errors.products?.[index]?.sell_price}
                          helperText={
                            errors.products?.[index]?.sell_price?.message || ""
                          }
                          onChange={(event) =>
                            handleProductProperties(
                              index,
                              event.target.value,
                              "sell_price"
                            )
                          }
                          sx={{
                            "& .MuiInputBase-root": {
                              border: "1px solid #D5D9E2",
                              backgroundColor: "#fff",
                              borderRadius: "7px",
                            },
                            "& .MuiInputBase-root::before": {
                              border: "none",
                            },
                            "& .MuiInputBase-root:hover::before": {
                              border: "none",
                            },
                          }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 4, md: 4, lg: 1 }} className="mt-2">
                      <IconButton
                        aria-label="remove"
                        onClick={() => handleRemoveProduct(product)}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </>
            )}
          />
          {/* buttons */}
          <Grid size={12} mt={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "10px",
              }}
              marginTop={5}
            >
              <Button
                onClick={handleReset}
                variant="text"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Reset
              </Button>

              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                  color: "#fff !important",
                }}
              >
                {selectedRow ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>
          <DevTool control={control} />
        </Box>
      </Box>
    </Modal>
  )
}

export default AddProductVariantModal
